import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { homeFor } from "@/lib/api";
import AppShell from "@/components/AppShell";
import Landing from "@/pages/Landing";
import Pricing from "@/pages/Pricing";
import { Login, Register, Forgot, Reset } from "@/pages/AuthPages";
import AuthCallback, { LinkedInCallback } from "@/pages/AuthCallback";
import { Features, UseCase, Faq, Demo } from "@/pages/Marketing";
import { captureVisit } from "@/lib/attribution";
import VerifyEmail from "@/pages/VerifyEmail";
import Unsubscribe from "@/pages/Unsubscribe";
import Onboarding from "@/pages/Onboarding";
import Status from "@/pages/Status";
import Dashboard from "@/pages/Dashboard";
import Jobs from "@/pages/Jobs";
import JobDetail from "@/pages/JobDetail";
import Applications from "@/pages/Applications";
import Agents from "@/pages/Agents";
import Autopilot from "@/pages/Autopilot";
import Profile from "@/pages/Profile";
import ResumeCheck from "@/pages/ResumeCheck";
import ResumeAts from "@/pages/ResumeAts";
import Privacy from "@/pages/Privacy";
import Billing from "@/pages/Billing";
import PaymentResult from "@/pages/PaymentResult";
import RecruiterPortal from "@/pages/RecruiterPortal";
import Talent from "@/pages/Talent";
import Admin from "@/pages/Admin";
import HourlyJobs from "@/pages/HourlyJobs";
import HourlyApp from "@/pages/HourlyApp";
import CompareSwipejobs from "@/pages/CompareSwipejobs";
import AreaJobs from "@/pages/AreaJobs";
import PublicJob from "@/pages/PublicJob";
import Partners, { EmbedWidget } from "@/pages/Partners";

const Loading = () => <div className="grid min-h-screen place-items-center text-muted-foreground" data-testid="app-loading">Loading…</div>;

function Protected({ roles, children }) {
  const { user } = useAuth();
  if (user === null) return <Loading />;
  if (!user) return <Navigate to="/login" replace />;
  if (!user.role_confirmed) return <Navigate to="/onboarding" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to={homeFor(user)} replace />;
  return children;
}

function AppRouter() {
  const location = useLocation();
  useEffect(() => { captureVisit(); }, []);
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  if (location.hash?.includes("li_code=")) return <LinkedInCallback />;
  const shell = (roles) => <Protected roles={roles}><AppShell /></Protected>;
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/pricing" element={<Pricing />} />
      <Route path="/features" element={<Features />} />
      <Route path="/faq" element={<Faq />} />
      <Route path="/demo" element={<Demo />} />
      <Route path="/for-candidates" element={<UseCase kind="candidates" />} />
      <Route path="/for-executives" element={<UseCase kind="executives" />} />
      <Route path="/for-recruiters" element={<UseCase kind="recruiters" />} />
      <Route path="/status" element={<Status />} />
      <Route path="/hourly-jobs" element={<HourlyJobs />} />
      <Route path="/compare/swipejobs" element={<CompareSwipejobs />} />
      <Route path="/jobs" element={<AreaJobs />} />
      <Route path="/jobs/in/:place" element={<AreaJobs />} />
      <Route path="/jobs/:field/in/:place" element={<AreaJobs />} />
      <Route path="/job/:jobId" element={<PublicJob />} />
      <Route path="/partners" element={<Partners />} />
      <Route path="/resume-ats-checker" element={<ResumeAts />} />
      <Route path="/embed/:place" element={<EmbedWidget />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/forgot-password" element={<Forgot />} />
      <Route path="/reset-password" element={<Reset />} />
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/unsubscribe" element={<Unsubscribe />} />
      <Route path="/onboarding" element={<Onboarding />} />
      <Route path="/payment/success" element={<PaymentResult />} />
      <Route path="/payment/cancel" element={<PaymentResult cancelled />} />
      <Route element={shell(["candidate", "admin"])}>
        <Route path="/app" element={<Dashboard />} />
        <Route path="/app/jobs" element={<Jobs />} />
        <Route path="/app/hourly" element={<HourlyApp />} />
        <Route path="/app/jobs/:jobId" element={<JobDetail />} />
        <Route path="/app/applications" element={<Applications />} />
        <Route path="/app/agents" element={<Agents />} />
        <Route path="/app/autopilot" element={<Autopilot />} />
        <Route path="/app/profile" element={<Profile />} />
        <Route path="/app/resume" element={<ResumeCheck />} />
      </Route>
      <Route element={shell(["candidate", "recruiter", "admin"])}>
        <Route path="/app/billing" element={<Billing />} />
        <Route path="/app/privacy" element={<Privacy />} />
      </Route>
      <Route element={shell(["recruiter", "admin"])}>
        <Route path="/recruiter" element={<RecruiterPortal />} />
        <Route path="/recruiter/talent" element={<Talent />} />
      </Route>
      <Route element={shell(["admin"])}>
        <Route path="/admin" element={<Admin />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
          <Toaster richColors position="top-right" />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}
